import React, { useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/style/colors";
import PrimaryButton from "../generic/PrimaryButton";
import {
  createSearchParams,
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as Logo } from "../../assets/icons/LogoLarge.svg";

import { useExpanded } from "../../hooks/useExpanded";
import { ReactComponent as MenuIcon } from "../../assets/icons/menuIcon.svg";
import { animate, AnimatePresence, motion, transform } from "framer-motion";
import MenuButton from "./MenuButton";
import SmallMenuButton from "./SmallMenuButton";
import { EXTERNAL_LINK, NORMAL_LINK } from "../../constants/constants";

export const headerButtons = [
  {
    buttonTitle: "Platform",
    category: [
      {
        id: 0,
        categoryTitle: null,
        categoryItems: [
          {
            title: "Measure",
            description:
              "Track user origins, monitor installs, reinstalls, and reactivations, and measure the success of your ad campaigns.",
            link: "/measure",
            linkType: NORMAL_LINK,
          },
          {
            title: "Analyse",
            description:
              "Transform performance data into strategic actions. Drive better results with in-depth reporting and attribution analysis.",
            link: "/analyse",
            linkType: NORMAL_LINK,
          },
          {
            title: "Engagement",
            description:
              "Create smart links that guide users directly to specific content in your app or App Stores, including deferred deep links.",
            link: "/engagement",
            linkType: NORMAL_LINK,
          },
          {
            title: "Cross-App Messaging",
            description:
              "Automate and deliver targeted messages across all your apps from a single platform",
            link: "/messaging",
            linkType: NORMAL_LINK,
          },
        ],
      },
    ],
  },

  {
    buttonTitle: "Solutions",
    category: [
      {
        id: 1,
        categoryTitle: "BY TEAM",
        categoryItems: [
          {
            title: "Marketing",
            description:
              "Drive user acquisition and engagement through targeted campaigns and deep links.",
            link: "/marketing",
            linkType: NORMAL_LINK,
          },
          {
            title: "Product",
            description:
              "Guide users to key app features and improve in-app experiences.",
            link: "/product",
            linkType: NORMAL_LINK,
          },
          {
            title: "Data & Analytics",
            description:
              "Track performance and user behaviour to make informed decisions.",
            link: "/data-analytics",
            linkType: NORMAL_LINK,
          },
        ],
      },

      {
        id: 2,
        categoryTitle: "BY GOAL",
        categoryItems: [
          {
            title: "App Growth",
            description:
              "Boost installs, reactivations, and long-term retention with smart links and detailed attribution analysis.",
            link: "/app-growth",
            linkType: NORMAL_LINK,
          },
          {
            title: "Increase engagement & LTV",
            description:
              "Deliver personalised messages and measure user impact across channels.",
            link: "/increase-engagement",
            linkType: NORMAL_LINK,
          },
        ],
      },

      {
        id: 3,
        categoryTitle: "BY PLATFORM",
        categoryItems: [
          {
            title: "Mobile",
            description:
              "Optimize experiences and track journeys across iOS and Android apps",
            link: "/platform-mobile",
            linkType: NORMAL_LINK,
          },
          {
            title: "Web devices",
            description:
              "Link web to app seamlessly and measure cross-platform user behaviour.",
            link: "/platform-web",
            linkType: NORMAL_LINK,
          },
        ],
      },
    ],
  },
  {
    buttonTitle: "Pricing",
    buttonLink: {
      link: "/pricing",
      linkType: NORMAL_LINK,
    },
    category: null,
  },
  {
    buttonTitle: "Why",
    category: [
      {
        id: 4,
        categoryTitle: null,
        categoryItems: [
          {
            title: "Use cases",
            description: "How teams use Grovs to drive growth and engagement.",
            link: "/usecase",
            linkType: NORMAL_LINK,
          },
          {
            title: "Privacy",
            description:
              "Grovs protects user data and complies with global standards like GDPR and CCPA.",
            linkType: NORMAL_LINK,
            link: "/privacy",
          },
          {
            title: "Security",
            description:
              "Your data is safe with end-to-end encryption and enterprise-grade protections.",
            linkType: NORMAL_LINK,
            link: "/security",
          },
          {
            title: "Service Level Agreements",
            description:
              "Ensure consistent performance, timely support, and clear accountability across services.",
            linkType: NORMAL_LINK,
            link: "/sla",
          },
        ],
      },
    ],
  },
  {
    buttonTitle: "Resources",
    category: [
      {
        id: 5,
        categoryTitle: null,
        categoryItems: [
          {
            title: "Blog",
            description: "Insights, updates, and industry best practices.",
            link: "/blog",
            linkType: NORMAL_LINK,
          },
          {
            title: "Developer Guide",
            description: "Comprehensive technical reference for developers.",
            link: "https://docs.grovs.io/s/docs",
            linkType: EXTERNAL_LINK,
          },
          {
            title: "Guides",
            description:
              "Integration guides to help you set up and deploy Grovs quickly.",
            link: "https://docs.grovs.io/s/docs/doc/migration-guides-hoflNvNs9F",
            linkType: EXTERNAL_LINK,
          },
          {
            title: "Marketing Documentation",
            description:
              "Complete reference guide for marketers on project setup and platform usage.",
            link: "https://docs.grovs.io/s/ba0bb3fe-9516-4b93-b183-a637282554f9",
            linkType: EXTERNAL_LINK,
          },
          {
            title: "Migrate from Firebase",
            description:
              "Easily switch from Firebase Dynamic Links to Grovs with compatible setup and tracking.",
            link: "/migrate-from-fireabse-dynamic-links",
            linkType: NORMAL_LINK,
          },
          {
            title: "Free Link Generator",
            description:
              "Create a free dynamic link that redirects users to your desired content based on their device type.",
            link: "/create",
            linkType: NORMAL_LINK,
          },
          // {
          //   title: "Migrate from Branch.io",
          //   description:
          //     "Quickly move your Branch.io links to Grovs without losing deep link functionality.",
          //   link: "/create",
          //   linkType: EXTERNAL_LINK,
          // },
        ],
      },
    ],
  },
];

const Header = ({ setIsBackgropVisible, isPageScrolled }) => {
  const [expanded, expand] = useExpanded();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigateTo = (to, type) => {
    switch (type) {
      case NORMAL_LINK:
        navigate(to);
        break;
      case EXTERNAL_LINK:
        window.open(to, "_blank");
        break;
      case "scroll":
        let element = document.getElementById(to);
        if (element) {
          element?.scrollIntoView({ behavior: "smooth" });
        } else {
          setSearchParams((params) => {
            params.append("scrollTo", to);
          });
          navigate({
            pathname: "/",
            search: createSearchParams(searchParams).toString(),
          });
        }

        break;

      default:
        break;
    }
  };

  const handleSmallMenuItemClick = (link, type) => {
    navigateTo(link, type);
    expand();
  };

  const animateHeaderItems = () => {
    const logo = document.getElementById("logoId");
    const headerBtn = document.getElementsByClassName("headerBtnId");
    const headerLogin = document.getElementById("headerLogin");
    const headerGetStarted = document.getElementById("headerGetStarted");

    animate(logo, { color: lightThemeColors.background });
    animate(headerBtn, { color: lightThemeColors.header.menuItemColor });
    animate(headerLogin, { color: lightThemeColors.header.menuItemColor });
    animate(headerGetStarted, {
      color: lightThemeColors.header.absoluteBlack,
      background: lightThemeColors.background,
    });
  };

  const animateScrolledHeader = () => {
    const logo = document.getElementById("logoId");
    const header = document.getElementById("headerId");
    const headerBtn = document.getElementsByClassName("headerBtnId");
    const headerLogin = document.getElementById("headerLogin");
    const headerGetStarted = document.getElementById("headerGetStarted");

    animate(header, { background: lightThemeColors.background });
    animate(logo, { color: lightThemeColors.darkColor });
    animate(headerBtn, { color: lightThemeColors.header.categoryTypeColor });
    animate(headerLogin, { color: lightThemeColors.header.categoryTypeColor });
    animate(headerGetStarted, {
      color: lightThemeColors.background,
      background: lightThemeColors.header.absoluteBlack,
    });
  };

  const animateHeaderBackground = (pathname) => {
    const element = document.getElementById("headerId");

    switch (pathname) {
      case "/":
        animate(element, { background: "transparent" });
        animateHeaderItems();
        break;
      case "/messaging":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.crossAppMessagingBg });
        animateHeaderItems();
        break;
      case "/engagement":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.engagementBg });
        animateHeaderItems();
        break;
      case "/migrate-from-fireabse-dynamic-links":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.engagementBg });
        animateHeaderItems();
        break;
      case "/measure":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.measureBg });
        animateHeaderItems();
        break;
      case "/analyse":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.analyseBg });
        animateHeaderItems();
        break;
      case "/product":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.engagementBg });
        animateHeaderItems();
        break;
      case "/data-analytics":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.landingPagePrimaryBackground });
        animateHeaderItems();
        break;
      case "/marketing":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.marketingBg });
        animateHeaderItems();
        break;
      case "/increase-engagement":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.analyseBg });
        animateHeaderItems();
        break;
      case "/app-growth":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.landingPagePrimaryBackground });
        animateHeaderItems();
        break;
      case "/platform-web":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.landingPagePrimaryBackground });
        animateHeaderItems();
        break;
      case "/platform-mobile":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.landingPagePrimaryBackground });
        animateHeaderItems();
        break;
      case "/usecase":
        animate(element, { background: "transparent" });
        // animate(element, { background: lightThemeColors.landingPagePrimaryBackground });
        animateHeaderItems();
        break;
      default:
        animateScrolledHeader();
        break;
    }
  };

  const animateHeader = (pathname, isScrolled) => {
    if (isScrolled) {
      animateScrolledHeader();
    } else {
      animateHeaderBackground(pathname);
    }
  };

  useEffect(() => {
    animateHeaderBackground(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    animateHeader(location.pathname, isPageScrolled);
  }, [location.pathname, isPageScrolled]);

  return (
    <FullPageContainer as={motion.div} id="headerId">
      <OuterWrapper>
        <Container>
          <Link to={"/"}>
            <TitleWrapper>
              <LogoWrapper>
                <Logo id="logoId" />
              </LogoWrapper>
            </TitleWrapper>
          </Link>

          <ButtonsWrapper>
            {headerButtons.map((button, index) => (
              <MenuButton
                key={index}
                setIsBackgropVisible={setIsBackgropVisible}
                title={button.buttonTitle}
                menuOptions={button.category}
                navigateTo={navigateTo}
                buttonLink={button.buttonLink}
              />
            ))}
          </ButtonsWrapper>

          <ButtonsWrapper style={{ marginLeft: "auto" }}>
            <HeaderBtn
              id="headerLogin"
              style={{ color: `${lightThemeColors.background}` }}
              onClick={() =>
                navigateTo("http://app.grovs.io/login", EXTERNAL_LINK)
              }
            >
              Log in
            </HeaderBtn>

            <PrimaryButton
              id="headerGetStarted"
              styled={{
                fontSize: "14px",
                padding: "6px 8px",
                color: lightThemeColors.header.absoluteBlack,
                background: lightThemeColors.background,
              }}
              onClick={() =>
                navigateTo("http://app.grovs.io/register", EXTERNAL_LINK)
              }
              text={"Get Started - it's free!"}
            />
          </ButtonsWrapper>

          <SmallScreenMenu>
            <PrimaryButton
              onClick={expand}
              styled={{
                color: lightThemeColors.background,
                padding: "10px",
                minWidth: "50px",
              }}
              icon={<MenuIcon />}
              bgVariant={lightThemeColors.darkColor}
            />
          </SmallScreenMenu>
        </Container>
      </OuterWrapper>

      <AnimatePresence>
        {expanded && (
          <>
            <MenuContentWrapper
              initial={{ height: 0 }}
              animate={{ height: "100%" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Container>
                <TitleWrapper
                  onClick={() => handleSmallMenuItemClick("/", NORMAL_LINK)}
                >
                  <LogoWrapper>
                    <Logo color={lightThemeColors.darkColor} />
                  </LogoWrapper>
                </TitleWrapper>

                <ButtonsWrapper style={{ marginLeft: "auto" }}>
                  <HeaderBtn
                    style={{ color: `${lightThemeColors.darkColor}` }}
                    onClick={() =>
                      handleSmallMenuItemClick(
                        "http://app.grovs.io/login",
                        EXTERNAL_LINK
                      )
                    }
                  >
                    Log in
                  </HeaderBtn>

                  <PrimaryButton
                    styled={{
                      padding: "10px 20px",
                      color: lightThemeColors.background,
                      background: lightThemeColors.darkColor,
                    }}
                    onClick={() =>
                      handleSmallMenuItemClick(
                        "http://app.grovs.io/register",
                        EXTERNAL_LINK
                      )
                    }
                    text={"Get started"}
                  />
                </ButtonsWrapper>

                <SmallScreenMenu>
                  <PrimaryButton
                    onClick={expand}
                    styled={{
                      color: lightThemeColors.background,
                      padding: "10px",
                      minWidth: "50px",
                    }}
                    icon={<MenuIcon />}
                    bgVariant={lightThemeColors.darkColor}
                  />
                </SmallScreenMenu>
              </Container>

              {headerButtons.map((button, index) => (
                <SmallMenuButton
                  key={index}
                  setIsBackgropVisible={setIsBackgropVisible}
                  title={button.buttonTitle}
                  menuOptions={button.category}
                  navigateTo={handleSmallMenuItemClick}
                  buttonLink={button.buttonLink}
                />
              ))}
            </MenuContentWrapper>
          </>
        )}
      </AnimatePresence>
    </FullPageContainer>
  );
};

export default Header;

const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 100;
  background: transparent;
  justify-content: center;
`;
const OuterWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1512px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 100px;
  position: relative;
  @media (max-width: 990px) {
    padding: 0 20px;
  }
`;
const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  width: 100%;
  max-width: 1512px;
  margin-left: auto;
  margin-right: auto;
  gap: 50px;
`;
const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  @media (max-width: 990px) {
    display: none;
  }
`;
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  p {
    color: ${lightThemeColors.darkColor};
    font-size: 20px;
    font-weight: 800;
    line-height: 28px;
    text-align: left;
  }
`;

const HeaderBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${lightThemeColors.header.menuItemColorFaded};
  font-size: 14px;
  font-weight: 500;
  line-height: 16.8px;
  text-align: left;
`;
const LogoWrapper = styled.div`
  color: white;
  svg {
    max-width: 120px;
  }
`;

const MenuContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${lightThemeColors.lightColor};
  width: 100%;
  padding: 0px 20px;
  overflow: auto;
  position: fixed;
  height: 100%;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

const SmallScreenMenu = styled.div`
  display: none;
  margin-left: auto;
  @media (max-width: 990px) {
    display: flex;
  }
`;
